import axios from "axios";
import {
  AUTH_BASE_URL,
  DELIVERY_BASE_URL,
  DEV_TOKEN,
  INTEGRATION_API_BASE_URL,
  INTEGRATION_BASE_URL,
  IS_DEV,
  LOGIN_REDIRECT_URL,
} from "./configApi";

export { AUTH_BASE_URL, INTEGRATION_API_BASE_URL, INTEGRATION_BASE_URL };

/* =========================================================
   AXIOS INSTANCES
   - Cookies HttpOnly envoyés automatiquement (withCredentials)
   - En IS_DEV, injecte aussi Authorization: Bearer <token> — même
     pattern que teqa_confirmation/src/api/auth.js
========================================================= */

const getDevToken = () =>  DEV_TOKEN;
// redirectOn401: seule l'instance AUTH_API doit faire perdre la session en
// cas de 401 — les autres services (integration/delivery/marketplace)
// peuvent être down/renvoyer une erreur sans jamais déconnecter l'utilisateur
// tant que auth.teqa.net répond correctement. Voir AuthContext/PrivateRoute
// qui ne dépendent que de getCurrentUser() (AUTH_API) pour le gating.
export const createAxiosInstance = (
  baseURL,
  { redirectOn401 = false, ...extraConfig } = {}
) => {
  const instance = axios.create({
    baseURL,
    withCredentials: true,
    ...extraConfig,
  });

  instance.interceptors.request.use((config) => {
    if (IS_DEV) {
      const token = getDevToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (redirectOn401 && error.response?.status === 401) {
        window.location.href = LOGIN_REDIRECT_URL;
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const AUTH_API = createAxiosInstance(AUTH_BASE_URL, {
  redirectOn401: true,
  xsrfCookieName: "csrftoken",     // match Django's cookie name
  xsrfHeaderName: "X-CSRFToken",   // match Django's expected header
});

export const INTEGRATION_API = createAxiosInstance(INTEGRATION_API_BASE_URL);

export const INTEGRATION_API_ = createAxiosInstance(INTEGRATION_BASE_URL);

/* =========================================================
   AUTH CALLS
========================================================= */

export const getCurrentUser = () =>
  AUTH_API.get("user/me/");

export const resolvePublicEntity = ({ type, id }) =>
  AUTH_API.get("entities/resolve/", { params: { type, id } });

// Recherche d'agences / agents de confirmation par nom (pour démarrer un chat)
export const searchPublicEntities = (q, types = "AGENCY_OWNER,AGENCY_AGENT") =>
  AUTH_API.get("entities/search/", { params: { q, types } });

export const logout = () =>
  AUTH_API.post("logout/");

/* =========================================================
   USER SETTINGS
========================================================= */

export const updateLanguage = (data) =>
  INTEGRATION_API.patch("user/language/", data);

export const updateProfile = (data) =>
  AUTH_API.patch("user/profile/", data);

export const checkEmail = (data) =>
  AUTH_API.post("check-email/", data);

export const checkUsername = (data) =>
  AUTH_API.post("check-username/", data);

export const getPermissionsCatalog = () =>
  AUTH_API.get("permissions/catalog/");

export const getChildUsers = () =>
  AUTH_API.get("user/children/");

export const createChildUser = (data) =>
  AUTH_API.post("user/children/create/", data);

export const updateChildUser = (userId, data) =>
  AUTH_API.patch(`user/children/${userId}/status/`, data);

/* =========================================================
   INTEGRATIONS
========================================================= */

export const integrationsList = () =>
  INTEGRATION_API.get("integrations/stores/");

export const integrationsAdd = (data) =>
  INTEGRATION_API.post("integrations/platforms/", data);

export const deleteIntegration = (id) =>
  INTEGRATION_API.delete(`integrations/stores/${id}/`);

export const productsList = (params = {}) =>
  INTEGRATION_API.get("integrations/products/", { params });

export const productsRemoteList = (sourceConnectionId, params = {}) =>
  INTEGRATION_API.get(`integrations/stores/${sourceConnectionId}/products/remote/`, { params });

export const productsImport = (sourceConnectionId, products) =>
  INTEGRATION_API.post(`integrations/stores/${sourceConnectionId}/products/import/`, {
    products,
  });

export const productsManualCreate = (data) =>
  INTEGRATION_API.post("integrations/products/manual/", data);

export const startYouCanOAuth = () =>
  INTEGRATION_API.post("integrations/oauth/youcan/start/");

/* =========================================================
   SHOPIFY CONNECT
========================================================= */

export const shopifyManualConnect = (data) =>
  INTEGRATION_API.post(
    "integrations/stores/shopify/manual/connect/",
    data
  );

/* =========================================================
   SHOPIFY WEBHOOKS
========================================================= */

export const shopifyWebhooksList = (integrationId) =>
  INTEGRATION_API.get(
    `wbhooks/shopify/${integrationId}/webhooks/`
  );

export const shopifyWebhooksCreate = (
  integrationId,
  body = { topic: "orders/create" }
) =>
  INTEGRATION_API.post(
    `wbhooks/shopify/${integrationId}/webhooks/`,
    body
  );

export const shopifyWebhooksDelete = (
  integrationId,
  body
) =>
  INTEGRATION_API.delete(
    `wbhooks/shopify/${integrationId}/webhooks/`,
    { data: body }
  );

/* =========================================================
   YOUCAN WEBHOOKS
========================================================= */

export const youcanWebhooksList = (integrationId) =>
  INTEGRATION_API.get(
    `integrations/youcan/${integrationId}/webhooks/`
  );

export const youcanWebhooksCreate = (
  integrationId,
  body = { event: "order.create" }
) =>
  INTEGRATION_API.post(
    `integrations/youcan/${integrationId}/webhooks/`,
    body
  );

export const youcanWebhooksDelete = (integrationId, event) =>
  INTEGRATION_API.delete(
    `wbhooks/youcan/${integrationId}/webhooks/`,
    {
      data: { event },
    }
  );

/* =========================================================
   CLIENTS
========================================================= */

export const clientsList = () =>
  INTEGRATION_API_.get("orders/clients/");

export const clientsCreate = (data) =>
  INTEGRATION_API_.post("orders/clients/", data);

export const clientsUpdate = (clientId, data) =>
  INTEGRATION_API_.patch(
    `orders/clients/${clientId}/`,
    data
  );

export const clientsDelete = (clientId) =>
  INTEGRATION_API_.delete(
    `orders/clients/${clientId}/`
  );

/* =========================================================
   ORDERS
========================================================= */

export const ordersList = () =>
  INTEGRATION_API_.get("orders/orders/");

export const ordersCreate = (data) =>
  INTEGRATION_API_.post("orders/orders/", data);

export const ordersDetail = (orderId) =>
  INTEGRATION_API_.get(
    `orders/orders/${orderId}/`
  );
export const orderUpdate = (id, payload) =>
  INTEGRATION_API_.patch(`orders/orders/${id}/`, payload);

// Historique du client dans CE store uniquement (dernière commande livrée,
// transporteur, date, totaux) — jamais l'historique des autres stores.
export const orderClientHistory = (orderId) =>
  INTEGRATION_API_.get(`orders/orders/${orderId}/client-history/`);

export const ordersDelete = (orderId) =>
  INTEGRATION_API_.delete(
    `orders/orders/${orderId}/`
  );
  export const ordersBatchUpdate = (payload) =>
  INTEGRATION_API_.post("orders/orders/", payload);

export const dispatchToPartner = (payload) =>
  INTEGRATION_API_.post("orders/orders/", payload);

export const ordersStats = (params) =>
  INTEGRATION_API_.get("orders/orders/", { params });

export const partnersList = (params) =>
  // INTEGRATION_API_.get("api/delivery-partners/", { params });
  ["ozonexpress", "dhl", "aramex", "fedex", "ups", "other"];
export const partnerCreate = (payload) =>
  INTEGRATION_API_.post("api/delivery-partners/", payload);


export const partnerUpdate = (id, payload) =>
  INTEGRATION_API_.patch(`api/delivery-partners/${id}/`, payload);

export const partnerDelete = (id) =>
  INTEGRATION_API_.delete(`api/delivery-partners/${id}/`);


/* =========================================================
   SPECIAL ORDER PATCH ENDPOINTS
========================================================= */

export const ordersDashboardStats = () =>
  INTEGRATION_API_.get("orders/orders/");

export const ordersUpdateStatus = (orderId, data) =>
  INTEGRATION_API_.patch(
    `orders/orders/${orderId}/status/`,
    data);

export const ordersUpdateDelivery = (orderId, data) =>
  INTEGRATION_API_.patch(
    `orders/orders/${orderId}/delivery/`,
    data
  );

export const ordersUpdateFlags = (orderId, data) =>
  INTEGRATION_API_.patch(
    `orders/orders/${orderId}/flags/`,
    data
  );

export const ordersUpdateNotes = (orderId, data) =>
  INTEGRATION_API_.patch(
    `orders/orders/${orderId}/notes/`,
    data
  );

export const ordersAssignCollaboration = (orderId, data) =>
  INTEGRATION_API_.post(`orders/orders/${orderId}/assign-collaboration/`, data);

export const scoring = (phone) =>
  INTEGRATION_API_.post(`orders/internal/client-scoring/`, { phone });
/* =========================================================
   LIVRAISON
========================================================= */

const DELIVERY_API = createAxiosInstance(DELIVERY_BASE_URL);

export const  updateOrderCity = (orderId, city) =>
  INTEGRATION_API_.patch(
    `orders/orders/${orderId}/`,
    city
  );
export const  updateShippingStatus = (orderId, status) =>
  INTEGRATION_API_.patch(
    `orders/orders/${orderId}/`,
    status
  );
export const  updateClientCity = (orderId, city) =>
  INTEGRATION_API_.patch(
    `orders/clients/${orderId}/`,
    city
  );
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
export const getAgencies = async () => {
  const res = await DELIVERY_API.get("core/agencies/");
  return res.data;
};
export const getAgency = async (agencyId) => {
  const res = await DELIVERY_API.get(`core/agencies/${agencyId}/`);
  return res.data;
};
export const getAgencyConnect = async () => {
  const res = await DELIVERY_API.get(`core/store-connections/`);
  return res.data;
};
export const addAgency = async (agencyData) => {
  const res = await DELIVERY_API.post("core/store-connections/", agencyData);
  return res.data;
};
export const updateAgency = async (agencyData,id) => {
  const res = await DELIVERY_API.patch(`core/store-connections/${id}/`, agencyData);
  return res.data;
};

// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
export const ameexCreateShipment = async (data) => {
  const res = await DELIVERY_API.post("ameex/shipments/add/", data);
  return res.data;
};

function isUuid(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || ""));
}

export const ameexParcelInfo = async (parcelCode, connectionId = null) => {
  const params = { parcel_code: parcelCode };

  if (isUuid(connectionId)) {
    params.connection_id = connectionId;
  }

  const res = await DELIVERY_API.get("ameex/shipments/info/", { params });
  return res.data;
};

export const ameexTracking = async (parcelCode, connectionId = null) => {
  const params = { parcel_code: parcelCode };

  if (isUuid(connectionId)) {
    params.connection_id = connectionId;
  }

  const res = await DELIVERY_API.get("ameex/shipments/tracking/", { params });
  return res.data;
};

export const ameexCities = async () => {
  const res = await DELIVERY_API.get("ameex/cities-fees/");
  return res.data;
};

// Connexion du compte AMEEX (identifiants vérifiés côté backend)
export const ameexConnect = async (data) => {
  const res = await DELIVERY_API.post("ameex/connections/", data);
  return res.data;
};

export const ameexConnections = async () => {
  const res = await DELIVERY_API.get("ameex/connections/");
  return res.data;
};

export const ameexUpdateConnection = async (connectionId, data) => {
  const res = await DELIVERY_API.patch(`ameex/connections/${connectionId}/`, data);
  return res.data;
};

export const ameexVerifyConnection = async (connectionId) => {
  const res = await DELIVERY_API.post(`ameex/connections/${connectionId}/verify/`);
  return res.data;
};
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
export const ozonCreateParcel = async (data) => {
  const res = await DELIVERY_API.post(
    "ozon/shipments/add-parcel/",
    data
  );
  return res.data;
};

export const ozonParcelInfo = async (trackingNumber, connectionId = null) => {

  const payload = {
    tracking_number: trackingNumber,
  };

  // agencyId peut valoir "OZON_EXPRESS" (delivery_agency_id de la commande) :
  // n'envoyer que de vrais UUID de connexion, sinon le backend répond 400.
  if (isUuid(connectionId)) {
    payload.connection_id = connectionId;
  }

  const res = await DELIVERY_API.post(
    "ozon/shipments/parcel-info/",
    payload
  );

  return res.data;
};

export const ozonTracking = async (trackingNumber) => {

  const res = await DELIVERY_API.post(
    "ozon/shipments/tracking/",
    {
      tracking_number: trackingNumber,
    }
  );

  return res.data;
};


export const ozonTrackingBulk = async (trackingNumbers) => {

  const res = await DELIVERY_API.post(
    "ozon/shipments/tracking/",
    {
      tracking_numbers: trackingNumbers,
    }
  );

  return res.data;
};

export const ozonCreateDeliveryNote = async (trackingNumbers, connectionId = null) => {

  const payload = {
    tracking_numbers: trackingNumbers,
  };

  if (connectionId) {
    payload.connection_id = connectionId;
  }

  const res = await DELIVERY_API.post(
    "ozon/delivery-notes/create/",
    payload
  );

  return res.data;
};
export const ozonCities = async () => {

  const res = await DELIVERY_API.get(
    "ozon/cities/"
  );

  return res.data;
};
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
export const onestaCities = async () => {
  const res = await DELIVERY_API.get(
    "onesta/cities/",
  );

  return res.data.data;
};


export const onestaCreateParcel = async (data) => {

  const res = await DELIVERY_API.post(
    "onesta/shipments/add-parcel/",
    data
  );

  return res.data;
};


export const onestaParcelInfo = async (code, connectionId = null) => {

  const payload = {
    code: code
  };

  if (isUuid(connectionId)) {
    payload.connection_id = connectionId;
  }

  const res = await DELIVERY_API.post(
    "onesta/shipments/parcel-info/",
    payload
  );

  return res.data;
};

export const onestaTracking = async (code, connectionId = null) => {

  const payload = {
    code: code
  };

  const res = await DELIVERY_API.post(
    "onesta/shipments/tracking/",
    payload
  );
  console.log("onestaTracking response", res);
  return res.data;
};
// |||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||||
export const forcelogHealth = async (connectionId = null) => {

  const params = {};

  if (connectionId) {
    params.connection_id = connectionId;
  }

  const res = await DELIVERY_API.get(
    "forcelog/health/",
    { params }
  );

  return res.data;
};

export const forcelogCities = async () => {
  const res = await DELIVERY_API.get(
    "/forcelog/cities/",
  );
  return res.data;
};

export const forcelogCreateParcel = async (data) => {

  const res = await DELIVERY_API.post(
    "forcelog/shipments/add-parcel/",
    data
  );

  return res.data;
};

export const forcelogParcelInfo = async (
  trackingCode,
) => {

  const params = {
    Code: trackingCode
  };
  const res = await DELIVERY_API.get(
    "forcelog/shipments/get-parcel/",
    { params }
  );

  return res.data;
};

// Historique complet du colis (timeline GetTracking)
export const forcelogTracking = async (trackingCode) => {
  const res = await DELIVERY_API.get("forcelog/shipments/tracking/", { params: { Code: trackingCode } });
  return res.data;
};

// Étiquette PDF 100x100 (blob) — ouvrir avec URL.createObjectURL
export const forcelogLabelPdf = async (trackingCode) => {
  const res = await DELIVERY_API.get("forcelog/shipments/label/", {
    params: { Code: trackingCode, pdf: 1 },
    responseType: "blob",
  });
  return res.data;
};

// |||||||||||||||||||||||||||cety||||||||||||||||||||||||||||||||||||||||||||||||
// Villes AMEEX via le backend (plus d'appel direct à api.ameex.app depuis le navigateur)
export const ameexcety = ameexCities;

// |||||||||||||||||||||||||||| SENDIT (delivery_service) ||||||||||||||||||||||||||||
// Les clés Sendit restent côté serveur (connexion : client_id = clé publique,
// api_key = clé privée, extra_config.pickup_district_id = quartier de ramassage).
export const senditCreateParcel = async (data) => {
  const res = await DELIVERY_API.post("sendit/shipments/add/", data);
  return res.data;
};

export const senditTracking = async (code, connectionId = null) => {
  const params = { code };
  if (isUuid(connectionId)) params.connection_id = connectionId;
  const res = await DELIVERY_API.get("sendit/shipments/tracking/", { params });
  return res.data;
};

export const senditVerifyConnection = async (connectionId) => {
  const res = await DELIVERY_API.post(`sendit/connections/${connectionId}/verify/`);
  return res.data;
};

// |||||||||||||||||||| LIVRAISON MULTI-AGENCES (API unifiée) ||||||||||||||||||||||
// Le front ne connaît plus les formats des transporteurs : capacités, villes,
// envoi et suivi passent par delivery_service/core.
export const carriersList = async () => (await DELIVERY_API.get("core/carriers/")).data;

export const carrierCities = async (connectionId) =>
  (await DELIVERY_API.get(`core/carriers/${connectionId}/cities/`)).data;

export const resolveCities = async (connectionId, cities) =>
  (await DELIVERY_API.post("core/cities/resolve/", { connection_id: connectionId, cities })).data;

export const saveCityMapping = async (connectionId, sourceName, cityId) =>
  (await DELIVERY_API.post("core/cities/mappings/", {
    connection_id: connectionId,
    source_name: sourceName,
    city_id: cityId,
  })).data;

// payload : { connection_id, order_id, receiver, phone, address, cod, city:{id,code,name},
//             comment, product, options:{open, fragile, exchange} }
export const createShipment = async (payload) => (await DELIVERY_API.post("core/shipments/", payload)).data;

// suivi avec l'agence ET le compte qui ont expédié (agency_code = repli pour les anciens colis)
export const trackShipment = async ({ tracking, orderId, agencyCode }) =>
  (await DELIVERY_API.get("core/shipments/tracking/", {
    params: { tracking, order_id: orderId, agency_code: agencyCode },
  })).data;

// |||||||||||||||||||||||||||| OLIVRAISON (delivery_service) ||||||||||||||||||||||||
// Connexion : client_id = apiKey, api_key = secretKey (JWT géré côté serveur).
export const olivraisonCities = async () => {
  const res = await DELIVERY_API.get("olivraison/cities/");
  return res.data;
};

export const olivraisonCreatePackage = async (data) => {
  const res = await DELIVERY_API.post("olivraison/shipments/add/", data);
  return res.data;
};

export const olivraisonTracking = async (trackingID, connectionId = null) => {
  const params = { trackingID };
  if (isUuid(connectionId)) params.connection_id = connectionId;
  const res = await DELIVERY_API.get("olivraison/shipments/tracking/", { params });
  return res.data;
};

export const olivraisonVerifyConnection = async (connectionId) => {
  const res = await DELIVERY_API.post(`olivraison/connections/${connectionId}/verify/`);
  return res.data;
};

export const olivraisonBlacklist = async (phone) => {
  const res = await DELIVERY_API.get(`olivraison/blacklist/${encodeURIComponent(phone)}/`);
  return res.data;
};

// |||||||||||||||||||||||||||| CATHEDIS (delivery_service) ||||||||||||||||||||||||||
export const cathedisCities = async () => {
  const res = await DELIVERY_API.get("cathedis/cities/");
  return res.data;
};

export const cathedisTracking = async (code, connectionId = null) => {
  const params = { code };
  if (isUuid(connectionId)) params.connection_id = connectionId;
  const res = await DELIVERY_API.get("cathedis/shipments/tracking/", { params });
  return res.data;
};

/* =========================================================
   DEFAULT EXPORT
========================================================= */

export default INTEGRATION_API;
